import React from 'react';
import { Icon } from 'semantic-ui-react';
import * as S from './styles';

export interface PottencialAvailableAssistance {
  id: string; // 'Basic' | 'Essential' | 'Complete'
  iof: number;
  grossAmount: number;
  commercialAmount: number;
}

export interface AssistanceSelectCardsProps {
  assistances: PottencialAvailableAssistance[];
  selected: string;
  setSelected: (selected: string) => void;
  validity: number;
}

// Detalhes das assistências
const ASSISTANCE_DETAILS: Record<
  string,
  {
    name: string;
    description: string;
    services: number;
    triggers: number;
    items: string[];
    icon: 'wrench' | 'settings' | 'shopping basket';
  }
> = {
  Basic: {
    name: 'Assistência Plena',
    description: 'Cobertura básica já inclusa no seguro, sem custo adicional.',
    services: 6,
    triggers: 5,
    icon: 'wrench',
    items: [
      'Eletricista',
      'Encanador',
      'Chaveiro',
      'Vidraceiro',
      'Desentupimento',
      'Desentupimento de tubulação de esgoto'
    ]
  },
  Essential: {
    name: 'Assistência Especial',
    description: 'Cobertura intermediária com mais serviços inclusos.',
    services: 16,
    triggers: 7,
    icon: 'settings',
    items: [
      'ASSISTÊNCIA PLENA',
      'Higienização de estofados',
      'Avaliação e adequação dos móveis',
      "Limpeza de caixa d'água",
      'Limpeza de calha',
      'Limpeza de painel solar',
      'Revisão de instalação elétrica',
      'Revisão de instalação hidráulica',
      'Instalação de olho mágico',
      'Avaliação e troca de tomadas, interruptores e soquetes de luz',
      'Help Desk (ilimitado)'
    ]
  },
  Complete: {
    name: 'Assistência Top',
    description: 'Cobertura completa com todos os serviços inclusos.',
    services: 24,
    triggers: 15,
    icon: 'shopping basket',
    items: [
      'ASSISTÊNCIA ESPECIAL',
      'Cobertura provisória de telhados',
      'Substituição de telhas',
      'Limpeza de Ar Condicionado',
      'Reparo de Ar Condicionado',
      'Conserto de linha branca e marrom',
      'Dedetização',
      'Descarte ecológico',
      'Consultoria ecológica (ilimitada)'
    ]
  }
};

function formatMoney(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

export const AssistanceSelectCards: React.FC<AssistanceSelectCardsProps> = ({
  assistances,
  selected,
  setSelected,
  validity
}) => {
  if (!assistances || assistances.length === 0) {
    return null;
  }

  // Ordenar assistências: Basic, Essential, Complete
  const sortedAssistances = [...assistances].sort((a, b) => {
    const order = ['Basic', 'Essential', 'Complete'];
    return order.indexOf(a.id) - order.indexOf(b.id);
  });

  return (
    <S.Wrapper>
      {sortedAssistances.map(assistance => {
        const details = ASSISTANCE_DETAILS[assistance.id];
        if (!details) return null;

        const isSelected = selected === assistance.id;
        const monthlyValue = validity > 0 ? assistance.grossAmount / validity : 0;
        const isFree = monthlyValue === 0;

        return (
          <S.Card
            key={assistance.id}
            isSelected={isSelected}
            onClick={() => setSelected(assistance.id)}
          >
            <S.CardHeader>
              <S.IconContainer>
                <Icon name={details.icon} size="large" style={{ color: '#E97425' }} />
              </S.IconContainer>
              <S.RadioButton isSelected={isSelected} />
            </S.CardHeader>

            <S.Title>{details.name}</S.Title>

            <S.PriceBadge isFree={isFree}>
              {isFree ? 'Incluso gratuitamente' : `+ ${formatMoney(monthlyValue)}/mês`}
            </S.PriceBadge>

            <S.Description>{details.description}</S.Description>

            <S.ServiceInfo>
              {details.services} serviços • {details.triggers} acionamentos
            </S.ServiceInfo>

            <S.ServicesTitle>Serviços Inclusos</S.ServicesTitle>
            <S.ServicesList>
              {details.items.map((item, index) => (
                <S.ServiceItem
                  key={index}
                  isHighlight={
                    item === 'ASSISTÊNCIA PLENA' || item === 'ASSISTÊNCIA ESPECIAL'
                  }
                >
                  {item}
                </S.ServiceItem>
              ))}
            </S.ServicesList>
          </S.Card>
        );
      })}
    </S.Wrapper>
  );
};

export default AssistanceSelectCards;


